/*
 * Copyright 2026 DirazCoder
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// structured error classes for parse()/format() failures.
//
// as of 0.9.0 every throw site on the parse/format data path —
// tokenize.ts, pattern.ts, format.ts, parse.ts (parse/safeParse/tryParse/
// parseToParts/compileParser), plus the two data-path throws in
// localeVocab.ts (partValue/assertNoCollision on the getLocaleVocab side)
// — throws one of these typed classes directly instead of a plain
// `new Error(message)`. every site we migrated kept its exact pre-0.9.0
// message text, and since subclasses of TemporalFmtError still pass
// `instanceof Error` and match the same message regexes (e.g.
// `/token "HH" requires/`), this didn't need a semver-major bump for
// either of those checks. what DOES break in 0.9.0: if anyone was
// specifically checking `err.constructor === Error` or `err.name ===
// 'Error'`, that'll now see a different name (e.g. 'FormatSyntaxError').
// noted in the 0.9.0 changelog.
//
// deliberately did NOT migrate:
//  - localeVocab.ts's registration-time throws (assertValidVocab,
//    registerLocaleVocab itself) — these are config-time API-misuse
//    errors on data a developer hands in once at startup, not runtime
//    parse/format failures, so they don't really fit this module's
//    TemporalFmtErrorCode taxonomy as-is. assertNoCollision is shared
//    between the registration path and the data path (getLocaleVocab)
//    though, so its throw stays a plain Error for both until that gets
//    split apart — see the tracking note where it's called.
//
// wrapUntypedError() below is still around for localeVocab.ts's
// registration throws, and for anything a caller passes into
// safeParse()/tryParse() from outside this package. on the data path
// itself, safeParse's `if (err instanceof TemporalFmtError) return { ok:
// false, error: err }` catches everything before it'd even reach the
// classifier here, so honestly the regex branches below are dead for
// parse/format/tokenize/pattern at this point — kept as the fallback for
// whatever hasn't been migrated to a typed throw site yet

export type TemporalFmtErrorCode =
  | 'FORMAT_SYNTAX_ERROR'
  | 'UNKNOWN_TOKEN'
  | 'PARSE_MISMATCH'
  | 'INVALID_DATE'
  | 'INVALID_TIME'
  | 'INVALID_OFFSET'
  | 'INVALID_TIME_ZONE'
  | 'INVALID_CALENDAR'
  | 'AMBIGUOUS_INPUT'
  | 'INVALID_LOCALE'
  | 'INVALID_DURATION';

export interface TemporalFmtErrorFields {
  code: TemporalFmtErrorCode;
  input?: string;
  format?: string;
  token?: string;
  position?: number;
  expected?: string;
  actual?: string;
  reason?: string;
}

// base class. `message` is the human-readable summary, the structured
// fields are the machine-readable bits a linter or codemod would report
// on. calling Error.captureStackTrace manually (where it exists) so the
// stack points at wherever this got thrown from, not at this constructor
// — same trick Node uses for its own error classes
export class TemporalFmtError extends Error {
  readonly code: TemporalFmtErrorCode;
  readonly input?: string;
  readonly format?: string;
  readonly token?: string;
  readonly position?: number;
  readonly expected?: string;
  readonly actual?: string;
  readonly reason?: string;

  constructor(message: string, fields: TemporalFmtErrorFields) {
    super(message);
    this.name = 'TemporalFmtError';
    this.code = fields.code;
    this.input = fields.input;
    this.format = fields.format;
    this.token = fields.token;
    this.position = fields.position;
    this.expected = fields.expected;
    this.actual = fields.actual;
    this.reason = fields.reason;
    // don't want the stack pointing at this constructor line — captureStackTrace
    // is V8-only though, so on other engines callers just get the default
    // stack (pointing here) since there's nothing better to do about it
    const capture = (Error as unknown as { captureStackTrace?: (target: Error, ctor?: Function) => void }).captureStackTrace;
    if (typeof capture === 'function') {
      capture(this, this.constructor);
    }
  }

  // lets callers just call err.toJSON() for logging. plain Error doesn't
  // serialize its non-enumerable fields on its own, so this picks them
  // up explicitly
  toJSON(): TemporalFmtErrorFields & { name: string; message: string } {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      input: this.input,
      format: this.format,
      token: this.token,
      position: this.position,
      expected: this.expected,
      actual: this.actual,
      reason: this.reason,
    };
  }
}

// each subclass just fixes `code` so callers can switch on it without
// having to re-check message text. the constructor only takes the
// fields that actually vary per call; `code` and the default message
// template come from the subclass itself

export class FormatSyntaxError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `format string "${fields.format ?? ''}" has a syntax error${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'FORMAT_SYNTAX_ERROR', ...rest },
    );
    this.name = 'FormatSyntaxError';
  }
}

export class UnknownTokenError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `token "${fields.token ?? ''}" is not a recognized temporal-fmt token${fields.format ? ` in format string "${fields.format}"` : ''}.`,
      { code: 'UNKNOWN_TOKEN', ...rest },
    );
    this.name = 'UnknownTokenError';
  }
}

export class ParseMismatchError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `input "${fields.input ?? ''}" does not match format "${fields.format ?? ''}"${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'PARSE_MISMATCH', ...rest },
    );
    this.name = 'ParseMismatchError';
  }
}

export class InvalidDateError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `input "${fields.input ?? ''}" does not describe a valid date${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'INVALID_DATE', ...rest },
    );
    this.name = 'InvalidDateError';
  }
}

/* c8 ignore start @preserve -- InvalidTimeError is part of the public
   error-class surface (exported from index.ts, code 'INVALID_TIME') but
   nothing in this package actually constructs one. went and checked
   whether it could get wired in the same way InvalidTimeZoneError just
   was (see parse.ts's zzz-validation loop) — does hour/minute/second have
   a post-match semantic range check the way zone ids do? nope.
   pattern.ts's regex fragments for HH/H/hh/h/mm/m/ss/s already enforce
   their valid ranges right at the regex level (e.g. HH is
   '(?:[01]\d|2[0-3])', which literally can't match "99"), so an
   out-of-range time just gets rejected as a plain shape mismatch before
   any semantic check would even run. no live gap to hook this into
   without inventing a redundant check purely to give this class a body.
   leaving it unconstructed until the library actually hits a real
   invalid-time case worth reporting */
export class InvalidTimeError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `input "${fields.input ?? ''}" does not describe a valid time${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'INVALID_TIME', ...rest },
    );
    this.name = 'InvalidTimeError';
  }
}
/* c8 ignore stop @preserve */

export class InvalidOffsetError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `offset "${fields.actual ?? ''}" is invalid${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'INVALID_OFFSET', ...rest },
    );
    this.name = 'InvalidOffsetError';
  }
}

export class InvalidTimeZoneError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `time zone "${fields.actual ?? ''}" is not a recognized IANA time zone or fixed offset${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'INVALID_TIME_ZONE', ...rest },
    );
    this.name = 'InvalidTimeZoneError';
  }
}

/* c8 ignore start @preserve -- InvalidCalendarError is part of the
   public error-class surface (exported from index.ts, code
   'INVALID_CALENDAR') but same story as InvalidTimeError above — nothing
   in this package constructs one. checked for a wiring opportunity the
   same way: there's no user-supplied calendar identifier anywhere in the
   library that we'd validate against a supported list. resolveCalendar()
   in parse.ts derives the calendar entirely from Intl's own resolution
   of the locale string — it's never handed an arbitrary "calendar" value
   a caller could actually get wrong. no live input to reject here.
   leaving it unconstructed until the library accepts an actual calendar
   parameter that could be invalid */
export class InvalidCalendarError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `calendar "${fields.actual ?? ''}" is not supported${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'INVALID_CALENDAR', ...rest },
    );
    this.name = 'InvalidCalendarError';
  }
}
/* c8 ignore stop @preserve */

export class AmbiguousInputError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `input "${fields.input ?? ''}" is ambiguous${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'AMBIGUOUS_INPUT', ...rest },
    );
    this.name = 'AmbiguousInputError';
  }
}

export class InvalidLocaleError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `locale "${fields.actual ?? ''}" is not a valid BCP-47 tag${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'INVALID_LOCALE', ...rest },
    );
    this.name = 'InvalidLocaleError';
  }
}

export class InvalidDurationError extends TemporalFmtError {
  constructor(fields: Omit<TemporalFmtErrorFields, 'code'> & { message?: string }) {
    const { message, ...rest } = fields;
    super(
      message ?? `duration is invalid${fields.reason ? `: ${fields.reason}` : ''}.`,
      { code: 'INVALID_DURATION', ...rest },
    );
    this.name = 'InvalidDurationError';
  }
}

// wraps a plain Error thrown from a code path that hasn't been migrated
// to typed errors yet. keeps the original message around in `reason` so
// callers reading the typed surface still see what actually failed.
// used by safeParse() in parse.ts.
//
// after the 0.9.0 migration, every throw site on the parse/format data
// path throws a TemporalFmtError directly, so safeParse's `instanceof
// TemporalFmtError` check always passes before this function would even
// get called — nothing in the current test suite actually reaches any
// branch below. kept as the safety net for whenever someone adds an
// unmigrated throw site down the line (see the c8-ignored call in
// parse.ts's safeParse) — same reasoning as that call site: removing
// this would silently break the "safeParse always returns a
// TemporalFmtError" contract the moment anyone adds a bare
// `throw new Error(...)` without wiring up a typed class for it
/* c8 ignore start @preserve -- unreachable from the current test suite,
   see rationale above */
export function wrapUntypedError(err: Error, context: { input?: string; format?: string }): TemporalFmtError {
  // try to classify by looking at the message — covers the existing
  // parse()/format() throw sites without having to touch them. anything
  // that doesn't match a known pattern falls through to a generic
  // ParseMismatchError, still with the structured fields intact
  const msg = err.message;
  if (/unknown token|isn't a recognized token/.test(msg)) {
    return new UnknownTokenError({ input: context.input, format: context.format, reason: msg });
  }
  if (/ambiguous/i.test(msg)) {
    return new AmbiguousInputError({ input: context.input, format: context.format, reason: msg });
  }
  if (/offset/.test(msg) && /out of range|exceeds|doesn't match the shape/i.test(msg)) {
    return new InvalidOffsetError({ input: context.input, format: context.format, reason: msg });
  }
  if (/no valid pattern matches/i.test(msg)) {
    return new ParseMismatchError({ input: context.input, format: context.format, reason: msg });
  }
  if (/doesn't describe a valid date\/time|incomplete date|weekday token|quarter token/.test(msg)) {
    return new InvalidDateError({ input: context.input, format: context.format, reason: msg });
  }
  const lowerMsg = msg.toLowerCase();
  const mentionsLocale = lowerMsg.includes('locale');
  if (
    (mentionsLocale && (lowerMsg.includes('produced no') || lowerMsg.includes('not a valid'))) ||
    lowerMsg.includes('cutoffs must be')
  ) {
    return new InvalidLocaleError({ input: context.input, format: context.format, reason: msg });
  }
  if (/format string exceeds maximum length|input exceeds maximum length|unterminated quote|isn't a recognized token/i.test(msg)) {
    return new FormatSyntaxError({ input: context.input, format: context.format, reason: msg });
  }
  return new ParseMismatchError({ input: context.input, format: context.format, reason: msg });
}
/* c8 ignore stop @preserve */